import React, { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import { getRoleInitials, mapAdminRoleDetail } from "./adminRoleDetailData"
import {
  buildDefaultPermissionsFromModules,
  mapPermissionGroupsToModules,
  mapSelectedIdsToPermissionSelections,
} from "./adminRolePermissionsData"
import RolePermissionsMatrix from "./RolePermissionsMatrix"

const STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
}

const USER_STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
}

/**
 * Admin Role Detail — GET /api/v1/admin/roles/get-single/:id
 */
const AdminRoleDetail = () => {
  const navigate = useNavigate()
  const { roleId } = useParams()
  const { can } = useAdminPermission()
  const canEdit = can("roles", "edit")
  const [role, setRole] = useState(null)
  const [modules, setModules] = useState([])
  const [permissionLevels, setPermissionLevels] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRole = useCallback(async () => {
    if (!roleId) return
    setIsLoading(true)
    setError(null)
    try {
      const [roleResponse, permissionsResponse] = await Promise.all([
        get(endpoints.getAdminRoleById(roleId), {
          params: { "with[]": ["permissions", "users"] },
        }),
        get(endpoints.getAdminPermissions),
      ])

      if (!roleResponse?.data?.success) {
        const message = toastApiError(roleResponse, "Failed to load role")
        setError(message)
        setRole(null)
        return
      }

      const mapped = mapAdminRoleDetail(roleResponse.data.result || {})
      setRole(mapped)

      if (permissionsResponse?.data?.success) {
        const nextModules = mapPermissionGroupsToModules(
          permissionsResponse.data.result || [],
        )
        setModules(nextModules)
        setPermissionLevels(
          mapSelectedIdsToPermissionSelections(
            nextModules,
            mapped?.permissionIds || [],
          ),
        )
      } else {
        setModules([])
        setPermissionLevels({})
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load role")
      setError(message)
      setRole(null)
    } finally {
      setIsLoading(false)
    }
  }, [roleId])

  useEffect(() => {
    document.title = "Role Detail | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    fetchRole()
  }, [fetchRole])

  const handleEditRole = () => {
    if (!role?.id) return
    navigate(`/admin/roles/${role.id}/edit`)
  }

  const handleViewProfile = user => {
    if (!user?.id) return
    navigate(`/admin/staff/${user.id}`)
  }

  if (isLoading) {
    return (
      <div
        className="admin-page admin-role-detail"
        aria-busy="true"
        aria-label="Loading role detail"
      >
        <div className="admin-role-detail__header">
          <div className="admin-role-detail__identity">
            <div className="admin-role-detail__skeleton admin-role-detail__skeleton--avatar" />
            <div className="admin-role-detail__skeleton admin-role-detail__skeleton--title" />
          </div>
          <div className="admin-role-detail__skeleton admin-role-detail__skeleton--btn" />
        </div>
        <div className="admin-role-detail__kpi-grid">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`kpi-skel-${index}`}
              className="admin-role-detail__skeleton admin-role-detail__skeleton--kpi"
            />
          ))}
        </div>
        <div className="admin-role-detail__skeleton admin-role-detail__skeleton--block" />
        <div className="admin-role-detail__permissions admin-role-detail__permissions--skeleton">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={`perm-skel-${index}`}
              className="admin-role-detail__skeleton admin-role-detail__skeleton--row"
            />
          ))}
        </div>
      </div>
    )
  }

  if (!role) {
    return (
      <div className="admin-page admin-role-detail">
        <EmptyState
          title="Role not found"
          description={
            error || "This role may have been removed or the link is invalid."
          }
          actionText="Try again"
          onActionClick={fetchRole}
        />
        <CustomButton
          variant="secondary"
          size="md"
          type="button"
          onClick={() => navigate("/admin/roles")}
        >
          Back to Roles
        </CustomButton>
      </div>
    )
  }

  const matrixPermissions =
    Object.keys(permissionLevels).length > 0
      ? permissionLevels
      : buildDefaultPermissionsFromModules(modules)

  return (
    <div className="admin-page admin-role-detail">
      <div className="admin-role-detail__header">
        <div className="admin-role-detail__identity">
          <div className="admin-role-detail__avatar" aria-hidden="true">
            {getRoleInitials(role.name)}
          </div>
          <div className="admin-role-detail__identity-copy">
            <h1 className="admin-role-detail__title">{role.name}</h1>
            <span
              className={`admin-role-detail__status admin-role-detail__status--${role.status}`}
            >
              <span
                className="admin-role-detail__status-dot"
                aria-hidden="true"
              />
              {STATUS_LABEL[role.status] || role.status}
            </span>
          </div>
        </div>

        {canEdit ? (
          <CustomButton
            variant="primary"
            size="md"
            type="button"
            trailingIconSrc={ICONS.edit}
            trailingIconAlt=""
            onClick={handleEditRole}
          >
            Edit Role
          </CustomButton>
        ) : null}
      </div>

      <div className="admin-role-detail__kpi-grid">
        <DashboardCard
          title="Users Assigned"
          tone="lightblue-custom"
          className="admin-role-detail__kpi-card"
          fullHeight
        >
          <p className="admin-role-detail__kpi-value">{role.users}</p>
        </DashboardCard>
        <DashboardCard
          title="Permissions Granted"
          tone="lightteal"
          className="admin-role-detail__kpi-card"
          fullHeight
        >
          <p className="admin-role-detail__kpi-value">
            {role.permissionsGranted}
          </p>
        </DashboardCard>
        <DashboardCard
          title="Last Updated"
          tone="purple-custom"
          className="admin-role-detail__kpi-card"
          fullHeight
        >
          <p className="admin-role-detail__kpi-value admin-role-detail__kpi-value--date">
            {role.lastUpdatedDisplay}
          </p>
        </DashboardCard>
      </div>

      <section className="admin-role-detail__description">
        <h2 className="admin-role-detail__section-title">Role Description:</h2>
        <p className="admin-role-detail__description-text">
          {role.description}
        </p>
      </section>

      <section className="admin-role-detail__permissions">
        <h2 className="admin-role-detail__section-title">Permissions</h2>
        <RolePermissionsMatrix
          modules={modules}
          permissions={matrixPermissions}
          readOnly
        />
      </section>

      <section className="admin-role-detail__assigned">
        <h2 className="admin-role-detail__section-title">Assigned to</h2>
        <div className="admin-role-detail__table-wrapper">
          <div className="admin-role-detail__table" role="table">
            <div
              className="admin-role-detail__table-row admin-role-detail__table-row--head"
              role="row"
            >
              <div role="columnheader">User Name</div>
              <div role="columnheader">City</div>
              <div role="columnheader">Status</div>
              <div role="columnheader">Assigned Date</div>
              <div
                role="columnheader"
                className="admin-role-detail__actions-head"
              >
                Actions
              </div>
            </div>

            {role.assignedUsers.map(user => (
              <div
                key={user.id}
                className="admin-role-detail__table-row"
                role="row"
              >
                <div className="admin-role-detail__user" role="cell">
                  <div
                    className="admin-role-detail__user-avatar"
                    aria-hidden="true"
                  >
                    {getRoleInitials(user.name)}
                  </div>
                  <TruncatedText className="admin-role-detail__user-name">
                    {user.name}
                  </TruncatedText>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-role-detail__cell"
                  role="cell"
                >
                  {user.city}
                </TruncatedText>
                <div className="admin-role-detail__status-cell" role="cell">
                  <span
                    className={`admin-role-detail__user-status admin-role-detail__user-status--${user.status}`}
                  >
                    {USER_STATUS_LABEL[user.status] || user.status}
                  </span>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-role-detail__cell"
                  role="cell"
                >
                  {user.assignedDate}
                </TruncatedText>
                <div className="admin-role-detail__actions" role="cell">
                  <CustomButton
                    variant="transparent"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight12}
                    trailingIconAlt=""
                    onClick={() => handleViewProfile(user)}
                  >
                    View Profile
                  </CustomButton>
                </div>
              </div>
            ))}

            {role.assignedUsers.length === 0 && (
              <div className="admin-role-detail__empty">
                <EmptyState
                  title="No users assigned"
                  description="Users assigned to this role will appear here."
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default AdminRoleDetail
